import { post, put, del, get } from '@/api'
import Vue from 'vue'
import { ApiHttpError } from '@/api'

export const OrderStatus = {
  CLOSED: 'closed',
  OPEN: 'open'
}

export default {
  namespaced: true,
  state: {
    orders: {},
    locks: JSON.parse(window.localStorage.getItem('order/locks')) || []
  },
  mutations: {
    ADD_ITEMS(state, { orderLines, orderToken }) {
      const order = state.orders[orderToken]

      if (order) {
        order.order_lines = order.order_lines.concat(orderLines)
      } else {
        Vue.set(state.orders, orderToken, {
          order_lines: [...orderLines]
        })
      }
    },

    UPDATE_ITEM(state, { orderLine, orderToken }) {
      const order = state.orders[orderToken]
      const orderLineToReplace = order.order_lines.find(
        orderLineToUpdate => orderLineToUpdate.token === orderLine.token
      )

      if (orderLineToReplace) {
        order.order_lines.splice(order.order_lines.indexOf(orderLineToReplace), 1, orderLine)
      }
    },

    REMOVE_ITEM(state, { orderToken, orderLineToken }) {
      const order = state.orders[orderToken]
      const orderLine = order.order_lines.find(orderLine => orderLine.token === orderLineToken)

      if (orderLine) {
        order.order_lines.splice(order.order_lines.indexOf(orderLine), 1)
      }
    },

    REMOVE(state, orderToken) {
      const order = state.orders[orderToken]

      if (order) {
        Vue.delete(state.orders, orderToken)
      }
    },

    REPLACE(state, { order, orderToken }) {
      Vue.set(state.orders, orderToken, order)
    },

    LOCK(state, orderToken) {
      const order = state.orders[orderToken]

      if (state.locks.indexOf(orderToken) === -1) {
        state.locks.push(orderToken)
        window.localStorage.setItem('order/locks', JSON.stringify(state.locks))
      }
      if (order) {
        order.is_locked = true
      }
    },

    UNLOCK(state, orderToken) {
      const order = state.orders[orderToken]
      const index = state.locks.indexOf(orderToken)

      if (index !== -1) {
        state.locks.splice(index, 1)
        window.localStorage.setItem('order/locks', JSON.stringify(state.locks))
      }
      if (order) {
        order.is_locked = false
      }
    },

    MARK_READY(state, { orderToken, customerToken }) {
      const order = state.orders[orderToken]
      order.order_lines.forEach(orderLine => {
        if (orderLine.customer_token === customerToken) {
          Vue.delete(orderLine, 'is_ready')
          Vue.set(orderLine, 'is_ready', true)
        }
      })
    },

    MARK_NOT_READY(state, { orderToken, customerToken }) {
      const order = state.orders[orderToken]
      order.order_lines.forEach(orderLine => {
        if (orderLine.customer_token === customerToken) {
          Vue.delete(orderLine, 'is_ready')
        }
      })
    }
  },
  actions: {
    async ADD_ITEM({ commit, dispatch, rootState, state }, { payload, orderToken }) {
      payload.customer_name = rootState.user.name
      payload.customer_token = rootState.user.client_token
      payload.order_token = orderToken

      const body = JSON.stringify(payload)
      try {
        const json = await post('order-lines', { body })
        if (!state.orders[orderToken]) {
          // On first add, remember the token
          dispatch('REMEMBER_TOKENS', { orderToken }, { root: true })
        }
        commit('ADD_ITEMS', { orderLines: json.data.order_lines, orderToken })
        return true
      } catch (error) {
        throw error
      }
    },

    async REMOVE_ITEM({ commit }, { orderLineToken, orderToken }) {
      try {
        await del(`order-lines/${orderLineToken}`)
        return commit('REMOVE_ITEM', { orderLineToken, orderToken })
        return true
      } catch (error) {
        throw error
      }
    },

    async UPDATE_ITEM({ commit }, { payload, orderLineToken, orderToken }) {
      payload.order_token = orderToken
      const body = JSON.stringify(payload)

      try {
        const json = await put(`order-lines/${orderLineToken}`, { body })
        const orderLine = json.data.order_line

        return commit('UPDATE_ITEM', { orderLine, orderToken })
        return true
      } catch (error) {
        throw error
      }
    },

    async FETCH({ commit, state, dispatch }, orderToken) {
      try {
        const json = await get(`orders/${orderToken}`)
        const order = json.data.order
        if (!state.orders[orderToken]) {
          // On first add, remember the token
          dispatch('REMEMBER_TOKENS', { orderToken }, { root: true })
        }
        commit('REPLACE', { order, orderToken })
        return order
      } catch (error) {
        if (error.status === 404) {
          dispatch('FORGET_TOKENS', orderToken, { root: true })
        }
        throw error
      }
    },

    async LOCK({ commit }, orderToken) {
      const body = JSON.stringify({ order_token: orderToken })

      try {
        await post('locked-orders', { body })
        commit('LOCK', orderToken)
        return true
      } catch (error) {
        throw error
      }
    },

    async UNLOCK({ commit }, orderToken) {
      const body = JSON.stringify({ order_token: orderToken })

      try {
        await post('unlocked-orders', { body })
        commit('UNLOCK', orderToken)
        return true
      } catch (error) {
        throw error
      }
    },

    async SEND_TO_KITCHEN({ dispatch }, orderToken) {
      const body = JSON.stringify({ order_token: orderToken })

      try {
        await post('prepared-orders', { body })
        // No way to set prepared_at on each orderLine, so re-fetch the order
        dispatch('FETCH', orderToken)
        return true
      } catch (error) {
        throw error
      }
    },

    async REGISTER_PAYMENT({ dispatch }, { orderToken, price, paymentId }) {
      const body = JSON.stringify({
        price,
        method: 'online',
        channel: 'external',
        parameters: {
          token: paymentId
        },
        order_token: orderToken
      })

      const onSuccess = () => {
        // No way to set payment on each orderLine, so re-fetch the order
        dispatch('FETCH', orderToken)
        return true
      }

      try {
        await post('payments', { body })

        return onSuccess()
      } catch (error) {
        if (error instanceof ApiHttpError && error.status === 400) {
          // 400 means payment was already received server to server
          // proceed as normal
          return onSuccess()
        } else {
          throw error
        }
      }
    },

    async CREATE_PAYMENT_LINK(store, { orderToken, price }) {
      const origin = window.location.origin
      const body = JSON.stringify({
        order_token: orderToken,
        price,
        continue_url: `${origin}/payment-callback/${orderToken}/${price}/continue?payment_id={payment_id}&callback=1`,
        cancel_url: `${origin}/payment-callback/${orderToken}/${price}/cancel?payment_id={payment_id}&callback=1`
      })

      try {
        const json = await post(`payment-links`, { body })
        const paymentLink = json.data
        return paymentLink
      } catch (error) {
        throw error
      }
    },

    async MARK_READY({ commit, rootState }, orderToken) {
      const customerToken = rootState.user.client_token
      const body = JSON.stringify({ order_token: orderToken, customer_token: customerToken })

      try {
        const success = await post('ready-orders', { body })
        commit('MARK_READY', { orderToken, customerToken })
        return true
      } catch (error) {
        throw error
      }
    },

    async MARK_NOT_READY({ commit, rootState }, orderToken) {
      const customerToken = rootState.user.client_token
      const body = JSON.stringify({ order_token: orderToken, customer_token: customerToken })

      try {
        await post('unready-orders', { body })
        commit('MARK_NOT_READY', { orderToken, customerToken })
        return true
      } catch (error) {
        throw error
      }
    },

    async CREATE_RATING(_, { orderToken, rating }) {
      const body = JSON.stringify({ order_token: orderToken, value: rating })

      try {
        const json = await post('ratings', { body })
        return json.data.rating.token
      } catch (error) {
        throw error
      }
    },

    async CREATE_RATING_COMMENT(_, { ratingToken, rating, comment }) {
      const body = JSON.stringify({ value: rating, comment })

      try {
        await put(`ratings/${ratingToken}`, { body })
        return true
      } catch (error) {
        throw error
      }
    }
  },
  getters: {
    // BY_TOKEN: state => orderToken => {
    //   const order = state.orders[orderToken]
    //   if (!order) {
    //     return null
    //   }
    //   const orderLines = order.order_lines || []
    //   const paidAmount = order.payments
    //     ? payments.reduce((totalPrice, payment) => {
    //         totalPrice += payment.price
    //         return totalPrice
    //       }, 0)
    //     : 0
    //   const [numSentOrderLines, numUnsentOrderLines, totalAmount] = orderLines.reduce(
    //     (acc, orderLine) => {
    //       if (orderLine.prepared_at) {
    //         // numSentOrderLines
    //         acc[0] += 1
    //       } else {
    //         // numUnsentOrderLines
    //         acc[1] += 1
    //       }
    //       // totalAmount
    //       acc[2] += orderLine.price || 0
    //       return acc
    //     },
    //     [0, 0, 0]
    //   )
    //   return {
    //     orderLines: orderLines,
    //     numOrderLines: orderLines.length,
    //     numSentOrderLines,
    //     numUnsentOrderLines,
    //     totalAmount,
    //     paidAmount,
    //     unpaidAmount: totalAmount - paidAmount,
    //     status: order.status,
    //     isClosed: order.status === OrderStatus.CLOSED,
    //     isSeated: !isNaN(parseInt(order.table_description, 10)),
    //     isLunch: order.is_lunch,
    //     isLocked: order.is_locked,
    //     restaurantName: order.restaurant_name,
    //     tableDescription: order.table_description
    //   }
    // }
  }
}
